import { HttpErrorResponse } from '@angular/common/http';
import { isPlatformBrowser } from '@angular/common';
import { Component, PLATFORM_ID, inject } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';

import { AppPreferences } from '../core/services/app-preferences';
import { ToastService } from '../core/toast/toast.service';
import { LandingHeader } from '../landing-page/components/landing-header/landing-header';
import { FundCalculatorApi } from '../personal-fund-request/services/fund-calculator-api';
import { OtpVerificationComponent } from '../shared/otp-verification/otp-verification';
import { LeadTrackingState } from './lead-tracking-state';

@Component({
  selector: 'app-tracking-otp',
  imports: [LandingHeader, OtpVerificationComponent],
  templateUrl: './tracking-otp.html',
  styleUrl: './tracking-otp.scss',
})
export class TrackingOtpComponent {
  readonly preferences = inject(AppPreferences);
  private readonly router = inject(Router);
  private readonly trackingState = inject(LeadTrackingState);
  private readonly fundCalculatorApi = inject(FundCalculatorApi);
  private readonly toast = inject(ToastService);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly pendingVerification = this.trackingState.getPendingVerification();

  otpCountdownSeconds = this.pendingVerification?.otpCountdownSeconds ?? 30;
  isSubmitting = false;
  isResending = false;
  otpError = '';

  constructor() {
    if (this.isBrowser && !this.pendingVerification) {
      void this.router.navigateByUrl('/');
    }
  }

  async verifyOtp(otp: string): Promise<void> {
    if (!this.pendingVerification || this.isSubmitting) {
      return;
    }

    this.isSubmitting = true;
    this.otpError = '';

    try {
      await firstValueFrom(
        this.fundCalculatorApi.verifyLeadOtp({
          nationalID: this.pendingVerification.nationalID,
          mobileNumber: this.pendingVerification.mobileNumber,
          otp,
        }),
      );
      this.trackingState.clearPendingVerification();
      await this.router.navigateByUrl('/my-requests');
    } catch (error) {
      this.otpError = this.getErrorMessage(error, 'otpVerification.verifyError');
    } finally {
      this.isSubmitting = false;
    }
  }

  async resendOtp(): Promise<void> {
    if (!this.pendingVerification || this.isResending) {
      return;
    }

    this.isResending = true;
    this.otpError = '';

    try {
      const result = await firstValueFrom(
        this.fundCalculatorApi.initLeadVerification({
          nationalID: this.pendingVerification.nationalID,
          mobileNumber: this.pendingVerification.mobileNumber,
        }),
      );

      this.otpCountdownSeconds = (result.resendOtpEveryMinutes ?? 0) * 60 || 30;
      this.trackingState.setPendingVerification({
        ...this.pendingVerification,
        otpCountdownSeconds: this.otpCountdownSeconds,
      });
    } catch (error) {
      if (this.isCustomerNotFoundError(error)) {
        this.otpCountdownSeconds = 30;
        return;
      }

      this.toast.error(this.getErrorMessage(error, 'otpVerification.resendError'));
    } finally {
      this.isResending = false;
    }
  }

  private getErrorMessage(error: unknown, fallbackKey: string): string {
    const fallbackMessage = this.preferences.t(fallbackKey);

    if (error instanceof HttpErrorResponse) {
      return this.extractApiMessage(error.error) || fallbackMessage;
    }

    if (error instanceof Error) {
      return this.isTechnicalErrorMessage(error.message)
        ? fallbackMessage
        : error.message || fallbackMessage;
    }

    return fallbackMessage;
  }

  private isTechnicalErrorMessage(message: string): boolean {
    return (
      message.startsWith('Http failure response') ||
      message === 'Failed to fetch' ||
      message === 'Could not verify OTP.' ||
      message === 'Could not send OTP.'
    );
  }

  private isCustomerNotFoundError(error: unknown): boolean {
    const message =
      error instanceof HttpErrorResponse
        ? this.extractApiMessage(error.error)
        : error instanceof Error
          ? error.message
          : '';
    const normalizedMessage = message.trim().toLowerCase();

    return (
      normalizedMessage.includes('لم يتم العثور على العميل') ||
      normalizedMessage.includes('customer not found') ||
      normalizedMessage.includes('client not found') ||
      normalizedMessage.includes('lead not found')
    );
  }

  private extractApiMessage(errorBody: unknown): string {
    if (typeof errorBody === 'string') {
      return errorBody.trim();
    }

    if (!errorBody || typeof errorBody !== 'object') {
      return '';
    }

    const body = errorBody as Record<string, unknown>;
    const message = body['message'];

    return typeof message === 'string' && message.trim() ? message.trim() : '';
  }
}
